import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Pencil, Trash2, Mail } from "lucide-react";
import {
  emailTemplatesList, emailTemplateCreate, emailTemplateUpdate,
  emailTemplateDelete, emailTemplateMergeFields,
} from "@/lib/api";
import { ActiveBadge, DeleteConfirm, inputCls } from "./RimsKit";
import { RichTextEditor } from "@/components/RichTextEditor";

const EMPTY = { name: "", subject: "", body: "", active: true };

// Convert a legacy plain-text template body into safe HTML so it can be edited
// in the rich editor. Escapes markup then preserves line breaks.
const legacyTextToHtml = (text) => {
  const esc = String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return esc.replace(/\r\n|\r|\n/g, "<br>");
};

export const EmailTemplatesManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState("");

  // Track the last-focused editable field + caret position so merge-field
  // buttons insert at the cursor rather than always appending.
  const subjectRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const activeFieldRef = useRef("body");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailTemplatesList(token);
      setItems(res.templates || []);
    } catch {
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    emailTemplateMergeFields(token)
      .then((r) => setMergeFields(r.merge_fields || []))
      .catch(() => {});
  }, [load, token]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openCreate = () => {
    setMode("create");
    setEditId(null);
    setForm({ ...EMPTY });
    activeFieldRef.current = "body";
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setMode("edit");
    setEditId(item.id);
    // Legacy plain-text templates are converted to HTML for the rich editor;
    // existing HTML templates load as-is.
    const bodyHtml =
      (item.body_format || "") === "html"
        ? item.body || ""
        : legacyTextToHtml(item.body || "");
    setForm({ name: item.name, subject: item.subject || "", body: bodyHtml, active: !!item.active });
    activeFieldRef.current = "body";
    setDialogOpen(true);
  };

  const insertMergeField = (token_) => {
    if (activeFieldRef.current === "subject") {
      const el = subjectRef.current;
      const current = form.subject || "";
      if (el && typeof el.selectionStart === "number") {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        set({ subject: current.slice(0, start) + token_ + current.slice(end) });
        requestAnimationFrame(() => {
          el.focus();
          const pos = start + token_.length;
          el.setSelectionRange(pos, pos);
        });
      } else {
        set({ subject: current + token_ });
      }
      return;
    }
    // Body -> insert into the rich-text editor at the caret.
    if (bodyEditorRef.current) bodyEditorRef.current.insertToken(token_);
  };

  const handleToggleActive = async (item) => {
    setTogglingId(item.id);
    setItems((prev) => prev.map((r) => (r.id === item.id ? { ...r, active: !r.active } : r)));
    try {
      await emailTemplateUpdate(token, item.id, { active: !item.active });
      toast.success(`${item.name} ${!item.active ? "activated" : "deactivated"}`);
    } catch {
      toast.error("Failed to update template");
      load();
    } finally {
      setTogglingId("");
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Template name is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        subject: form.subject,
        body: form.body,
        body_format: "html",
        active: form.active,
      };
      if (mode === "edit") {
        await emailTemplateUpdate(token, editId, payload);
        toast.success("Template updated");
      } else {
        await emailTemplateCreate(token, payload);
        toast.success("Template created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await emailTemplateDelete(token, deleteTarget.id);
      toast.success("Template deleted");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete template");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="email-templates-manager">
      <div className="flex justify-between items-center mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Mail size={18} className="text-[#1E6BFF] shrink-0" />
          <p className="text-white/50 text-sm">
            Reusable outbound email templates. Merge fields auto-fill from the prospect when used in the Prospect Workspace.
          </p>
        </div>
        <Button
          data-testid="email-template-add-button"
          onClick={openCreate}
          className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg h-10 shrink-0"
        >
          <Plus size={16} className="mr-1" /> New Template
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden" style={{ backgroundColor: "#0F1424" }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#1E6BFF]" size={26} />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-white/50" data-testid="email-templates-empty">
            No email templates yet. Create your first outreach template.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/60 w-16">Active</TableHead>
                  <TableHead className="text-white/60">Template</TableHead>
                  <TableHead className="text-white/60 hidden md:table-cell">Subject</TableHead>
                  <TableHead className="text-white/60 hidden lg:table-cell w-24">Status</TableHead>
                  <TableHead className="text-white/60 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} data-testid="email-template-row" className="border-white/10 hover:bg-white/5">
                    <TableCell>
                      <Switch
                        data-testid={`email-template-toggle-${r.id}`}
                        checked={!!r.active}
                        disabled={togglingId === r.id}
                        onCheckedChange={() => handleToggleActive(r)}
                        className="data-[state=checked]:bg-[#2EE59D]"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-white">{r.name}</div>
                      {r.created_by_name && (
                        <div className="text-white/40 text-xs">by {r.created_by_name}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-white/70 hidden md:table-cell max-w-[320px] truncate">
                      {r.subject || <span className="text-white/30">—</span>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <ActiveBadge active={r.active} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`email-template-edit-${r.id}`}
                          onClick={() => openEdit(r)}
                          className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10"
                        >
                          <Pencil size={15} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`email-template-delete-${r.id}`}
                          onClick={() => setDeleteTarget(r)}
                          className="h-8 w-8 text-white/60 hover:text-[#FF5C7A] hover:bg-white/10"
                        >
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-white/40 text-xs mt-3">{items.length} template{items.length === 1 ? "" : "s"}</p>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#0F1424] border-white/10 text-white" data-testid="email-template-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {mode === "edit" ? "Edit Template" : "New Template"}
            </DialogTitle>
            <DialogDescription className="text-white/45">
              Format your email with bold, italics, lists, and links. Insert merge fields to personalize each outbound email automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-1">
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs font-medium">Template Name *</Label>
              <Input
                data-testid="email-template-name-input"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="e.g. Intro Outreach"
                className={`${inputCls} h-10`}
              />
            </div>

            {/* Merge field palette */}
            {mergeFields.length > 0 && (
              <div className="rounded-lg border border-white/10 bg-[#0A0F1E] p-3">
                <div className="text-white/50 text-xs mb-2">
                  Insert merge field (adds to the last-focused Subject or Body):
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {mergeFields.map((mf) => (
                    <button
                      key={mf.token}
                      type="button"
                      data-testid={`email-template-merge-${mf.label.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => insertMergeField(mf.token)}
                      title={mf.source}
                      className="text-xs rounded-md border border-[#1E6BFF]/30 bg-[#1E6BFF]/10 text-[#8FB3FF] px-2 py-1 hover:bg-[#1E6BFF]/20 transition-colors"
                    >
                      {mf.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs font-medium">Subject</Label>
              <Input
                ref={subjectRef}
                data-testid="email-template-subject-input"
                value={form.subject}
                onFocus={() => { activeFieldRef.current = "subject"; }}
                onChange={(e) => set({ subject: e.target.value })}
                placeholder="Quick idea for {{Company}}"
                className={`${inputCls} h-10`}
              />
            </div>

            <div
              className="space-y-1.5"
              onFocusCapture={() => { activeFieldRef.current = "body"; }}
            >
              <Label className="text-white/70 text-xs font-medium">Body</Label>
              <RichTextEditor
                ref={bodyEditorRef}
                testid="email-template-body-input"
                value={form.body}
                onChange={(html) => set({ body: html })}
                placeholder="Hi {{FirstName}}, ..."
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0A0F1E] px-3 py-2.5">
              <span className="text-white/70 text-sm">Active (available for use)</span>
              <Switch
                data-testid="email-template-active-switch"
                checked={form.active}
                onCheckedChange={(v) => set({ active: v })}
                className="data-[state=checked]:bg-[#2EE59D]"
              />
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-white/70 hover:text-white hover:bg-white/5">
              Cancel
            </Button>
            <Button
              data-testid="email-template-save-button"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg"
            >
              {saving ? <Loader2 className="animate-spin mr-1" size={15} /> : null}
              {mode === "edit" ? "Save Changes" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirm
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete template?"
        description={`This will permanently remove "${deleteTarget?.name}". This cannot be undone.`}
        onConfirm={handleDelete}
        busy={deleting}
        testid="email-template-delete-dialog"
      />
    </div>
  );
};

export default EmailTemplatesManager;
